import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

declare global {
  interface Window { hcaptcha: any; }
}

@Injectable({
  providedIn: 'root'
})
export class HCaptchaService {
  private _widgetID: string;
  private _passiveWidgetID: string; // for the other login requests (OTP, login after registration, etc.)
  private _passiveCaptcha = '';
  private _onLoad$: Observable<void>;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    @Inject(DOCUMENT) private document: Document
  ) {}

  private _loadScript() {
    this._onLoad$ = new Observable((subscriber) => {
      const scriptElement = this.document.createElement('script');
      scriptElement.async = true;
      scriptElement.src = 'https://js.hcaptcha.com/1/api.js';
      scriptElement.onload = () => {
        subscriber.next();
        subscriber.complete();
      };
      scriptElement.onerror = () => {
        subscriber.error('Error appending hCaptcha script');
      };
      this.document.head.appendChild(scriptElement);
    });
  }

  init() {
    if (this.isCaptchaEnabled) {
      this._loadScript();
    }
  }

  remove() {
    if (this._widgetID) {
      window.hcaptcha?.remove();
    }
  }

  renderCaptcha(containerID: string, passiveContainerID: string) {
    return this._onLoad$?.subscribe(() => {
      this._widgetID = window.hcaptcha?.render(containerID, {
        sitekey: environment.hcaptcha.siteKey,
        // TODO: change with localeService
        hl: this.locale === 'ar' ? 'ar' : 'en',
      });

      this._passiveWidgetID = window.hcaptcha?.render(passiveContainerID, {
        sitekey: environment.hcaptcha.passiveSiteKey,
        // TODO: change with localeService
        hl: this.locale === 'ar' ? 'ar' : 'en',
      });
    });
  }

  getCaptcha(): Promise<string> {
    return new Promise((resolve, reject) => {
      window.hcaptcha?.execute(this._widgetID, { async: true })
        .then(response => {
          resolve(response?.response || '');
        }, err => {
          reject(err);
        });
    });
  }

  generatePassiveCaptcha() {
    window.hcaptcha?.execute(this._passiveWidgetID, { async: true })
      .then(response => {
        this._passiveCaptcha = response?.response || '';
      });
  }

  get passiveCaptcha(): string {
    this.generatePassiveCaptcha();
    return this._passiveCaptcha;
  }

  get isCaptchaEnabled(): boolean {
    return environment.hcaptcha.enabled;
  }
}
